import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { broadcastAPI } from '../../api';
import { Mail, Send, Loader2, History, FlaskConical, Newspaper } from 'lucide-react';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso || '';
  }
};

/**
 * BroadcastPage — email your subscribers:
 *  1. One-off broadcast composer (subject + message + optional button),
 *     with a "send test to me" step before the real blast.
 *  2. Weekly digest (auto-compiled from the last 7 days of posts).
 *  3. Send history for both.
 */
const BroadcastPage = () => {
  const { toast } = useToast();

  const [subscriberCount, setSubscriberCount] = useState(0);
  const [digestPosts, setDigestPosts] = useState(null);

  // Composer state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Digest state
  const [digestSending, setDigestSending] = useState(false);

  // History
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const [bRes, dRes] = await Promise.all([
        broadcastAPI.history().catch(() => ({ data: [] })),
        broadcastAPI.digestHistory().catch(() => ({ data: [] })),
      ]);
      const merged = [
        ...(bRes.data || []).map((r) => ({ ...r, type: 'broadcast' })),
        ...(dRes.data || []).map((r) => ({ ...r, type: 'digest' })),
      ].sort((a, b) => (b.started_at || '').localeCompare(a.started_at || ''));
      setHistory(merged.slice(0, 12));
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    broadcastAPI.digestPreview()
      .then((r) => {
        setSubscriberCount(r.data.subscriber_count || 0);
        setDigestPosts(r.data.posts || []);
      })
      .catch(() => setDigestPosts([]));
    loadHistory();
  }, [loadHistory]);

  const composerValid = subject.trim().length >= 3 && body.trim().length >= 10;

  const sendTest = async () => {
    if (!composerValid) {
      toast({ title: 'Add a subject and message first', variant: 'destructive' });
      return;
    }
    setTestSending(true);
    try {
      await broadcastAPI.sendTest({ subject, body, cta_label: ctaLabel, cta_url: ctaUrl, test_email: testEmail });
      toast({ title: 'Test sent', description: `Check the inbox of ${testEmail}.` });
    } catch (e) {
      toast({ title: 'Test failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setTestSending(false);
    }
  };

  const sendBroadcast = async () => {
    setSending(true);
    try {
      const res = await broadcastAPI.send({ subject, body, cta_label: ctaLabel, cta_url: ctaUrl });
      toast({ title: 'Broadcast queued', description: res.data.message });
      setConfirmOpen(false);
      setSubject(''); setBody(''); setCtaLabel(''); setCtaUrl('');
      setTimeout(loadHistory, 1500);
    } catch (e) {
      toast({ title: 'Send failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const res = await broadcastAPI.digestSend();
      toast({ title: 'Digest queued', description: res.data.message || `Sending to ${res.data.subscriber_count} subscribers…` });
      setTimeout(loadHistory, 1500);
    } catch (e) {
      toast({ title: 'Digest send failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setDigestSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="broadcast-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Broadcast</h1>
        <p className="text-sm text-slate-500 mt-1">
          Email your <span className="font-semibold text-slate-700">{subscriberCount.toLocaleString()}</span> active subscriber{subscriberCount === 1 ? '' : 's'} — a one-off announcement or the weekly digest.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* One-off composer */}
        <div className="lg:col-span-2 bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="broadcast-composer">
          <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
            <Mail size={16} className="text-sky-500" /> One-off broadcast
          </h3>
          <p className="text-xs text-slate-500 mb-5">
            Branded Paisley.org.uk email with one-click unsubscribe footer. Blank lines become paragraphs.
          </p>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Big news from Paisley this week…"
                className="mt-1"
                data-testid="broadcast-subject-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Message</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder={"Hi everyone,\n\nWrite your announcement here. Leave a blank line between paragraphs.\n\n— The Paisley.org.uk Team"}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="broadcast-body-input"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Button label (optional)</label>
                <Input
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  placeholder="Read more"
                  className="mt-1"
                  data-testid="broadcast-cta-label-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Button link (optional)</label>
                <Input
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="https://paisley.org.uk/news/…"
                  className="mt-1"
                  data-testid="broadcast-cta-url-input"
                />
              </div>
            </div>

            {/* Test send */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                <FlaskConical size={13} className="text-violet-500" /> Send a test first (recommended)
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 bg-white"
                  data-testid="broadcast-test-email-input"
                />
                <Button
                  variant="outline"
                  onClick={sendTest}
                  disabled={testSending || !testEmail || !composerValid}
                  className="bg-white"
                  data-testid="broadcast-test-btn"
                >
                  {testSending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FlaskConical size={14} className="mr-1.5" />}
                  {testSending ? 'Sending…' : 'Send test'}
                </Button>
              </div>
            </div>

            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!composerValid || subscriberCount === 0}
              className="w-full bg-sky-500 hover:bg-sky-400"
              data-testid="broadcast-send-btn"
            >
              <Send size={15} className="mr-2" />
              Send to {subscriberCount.toLocaleString()} subscriber{subscriberCount === 1 ? '' : 's'}
            </Button>
          </div>
        </div>

        {/* Right column: digest + history */}
        <div className="space-y-6">
          {/* Weekly digest */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="digest-panel">
            <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
              <Newspaper size={16} className="text-emerald-500" /> Weekly digest
            </h3>
            <p className="text-xs text-slate-500 mb-4">Auto-compiled from the last 7 days of published posts.</p>
            {digestPosts === null ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            ) : digestPosts.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
                No posts published in the last 7 days — publish something before sending the digest.
              </p>
            ) : (
              <div className="space-y-1.5 mb-4 max-h-44 overflow-y-auto">
                {digestPosts.map((p) => (
                  <div key={p.slug} className="bg-slate-50 rounded-lg p-2 flex items-center gap-2 text-xs">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
                    <span className="text-slate-800 truncate">{p.title}</span>
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="outline"
              onClick={sendDigest}
              disabled={digestSending || !digestPosts || digestPosts.length === 0 || subscriberCount === 0}
              className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              data-testid="digest-send-btn"
            >
              {digestSending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
              {digestSending ? 'Queuing…' : 'Send digest now'}
            </Button>
          </div>

          {/* History */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="broadcast-history">
            <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2">
              <History size={16} className="text-slate-400" /> Send history
            </h3>
            {history.length === 0 ? (
              <p className="text-xs text-slate-400">Nothing sent yet.</p>
            ) : (
              <div className="space-y-2.5">
                {history.map((r) => (
                  <div key={r.id} className="text-xs border-b border-slate-100 pb-2.5 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-semibold px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${
                        r.type === 'digest' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'
                      }`}>
                        {r.type}
                      </span>
                      <span className="text-slate-400">{fmtDate(r.started_at)}</span>
                    </div>
                    {r.subject && <p className="text-slate-700 font-medium mt-1 truncate">{r.subject}</p>}
                    <p className="text-slate-500 mt-0.5">
                      Sent {r.sent ?? 0}/{r.subscriber_count ?? 0}{r.failed ? ` · ${r.failed} failed` : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirm send modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="broadcast-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send size={18} className="text-sky-500" /> Confirm broadcast
            </DialogTitle>
            <DialogDescription>
              This emails every active subscriber. There's no undo once it's queued.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="bg-sky-50 border border-sky-100 rounded-lg p-3">
              <p className="text-sm text-sky-900">
                <span className="font-bold text-lg">{subscriberCount.toLocaleString()}</span> subscriber{subscriberCount === 1 ? '' : 's'} will receive:
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm font-bold text-slate-900 truncate">{subject}</p>
              <p className="text-xs text-slate-600 mt-1 line-clamp-3 whitespace-pre-line">{body}</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending} data-testid="broadcast-confirm-cancel">
              Cancel
            </Button>
            <Button onClick={sendBroadcast} disabled={sending} className="bg-sky-500 hover:bg-sky-400" data-testid="broadcast-confirm-send">
              {sending ? <><Loader2 size={14} className="mr-2 animate-spin" /> Queuing…</> : <><Send size={14} className="mr-2" /> Send now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BroadcastPage;
